'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import ReCAPTCHA from 'react-google-recaptcha'
import { BsStar } from 'react-icons/bs'
import { EVT, track } from '@/lib/analytics'
import { AuthPromptModal } from '@/components/auth-prompt-modal'
import {
  RatingSuccessModal,
  type SubmittedAttributeRating,
} from '@/components/rating-success-modal'
import type { SuggestedEntity } from '@/lib/suggestions'
import type { StreakInfo } from '@/lib/streaks'
import type { CompletionRing } from '@/lib/completion'
import type { ShareTopRating } from '@/lib/rating-share'
import { isRecaptchaBypassed } from '@/lib/recaptcha'

interface AttributeDefinition {
  id: string
  name: string
  description: string | null
  minValue: number
  maxValue: number
  weight: number
}

interface EntityAttribute {
  id: string
  attributeDefinition: AttributeDefinition
  averageRating: number
  totalRatings: number
}

interface RatingFormProps {
  entityId: string
  entitySlug: string
  entityName: string
  entityType: string
  attributes: EntityAttribute[]
  onSuccess?: () => void
  isGuest?: boolean
  guestAlreadyRated?: boolean
  suggestions?: SuggestedEntity[]
}

const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''
const skipRecaptcha = isRecaptchaBypassed()
const captchaReady = skipRecaptcha || Boolean(siteKey)

export function RatingForm({
  entityId,
  entitySlug,
  entityName,
  entityType,
  attributes,
  onSuccess,
  isGuest = false,
  guestAlreadyRated = false,
  suggestions = [],
}: RatingFormProps) {
  const router = useRouter()
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [ratings, setRatings] = useState<Record<string, number>>({})
  const [comment, setComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [submittedOverall, setSubmittedOverall] = useState(0)
  const [submittedAttributes, setSubmittedAttributes] = useState<SubmittedAttributeRating[]>([])
  const [streak, setStreak] = useState<StreakInfo | null>(null)
  const [collection, setCollection] = useState<CompletionRing | null>(null)
  const [topOtherRatings, setTopOtherRatings] = useState<ShareTopRating[]>([])

  const [hoveredStars, setHoveredStars] = useState<Record<string, number>>({})
  // Progressive star demo on the first attribute until the user interacts
  const [demoCount, setDemoCount] = useState(0)
  const [demoActive, setDemoActive] = useState(true)
  const firstAttributeId = attributes[0]?.id

  useEffect(() => {
    if (!demoActive || !firstAttributeId) return
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDemoActive(false)
      return
    }

    let count = 0
    let pauseTicks = 0
    const id = window.setInterval(() => {
      // After reaching 5, pause briefly then reset
      if (count >= 5) {
        pauseTicks += 1
        if (pauseTicks >= 3) {
          count = 0
          pauseTicks = 0
          setDemoCount(0)
        }
        return
      }
      count += 1
      setDemoCount(count)
    }, 320)

    return () => window.clearInterval(id)
  }, [demoActive, firstAttributeId])

  const stopDemo = () => {
    if (demoActive) {
      setDemoActive(false)
      setDemoCount(0)
    }
  }

  const handleRatingChange = (attributeId: string, value: number) => {
    if (attributeId === firstAttributeId) stopDemo()
    setRatings((prev) => ({ ...prev, [attributeId]: value }))
  }

  const handleStarHover = (attributeId: string, value: number) => {
    if (attributeId === firstAttributeId) stopDemo()
    setHoveredStars((prev) => ({ ...prev, [attributeId]: value }))
  }

  const handleStarLeave = (attributeId: string) => {
    setHoveredStars((prev) => {
      const newState = { ...prev }
      delete newState[attributeId]
      return newState
    })
  }

  const calculateOverallRating = () => {
    let totalWeight = 0
    let weightedSum = 0

    attributes.forEach((attr) => {
      const rating = ratings[attr.id]
      if (rating) {
        totalWeight += attr.attributeDefinition.weight
        weightedSum += rating * attr.attributeDefinition.weight
      }
    })

    return totalWeight > 0 ? (weightedSum / totalWeight).toFixed(1) : '0.0'
  }

  const openSuccessDialog = (
    nextStreak?: StreakInfo | null,
    nextCollection?: CompletionRing | null,
    nextTopOther?: ShareTopRating[]
  ) => {
    const overall = Number(calculateOverallRating())
    const breakdown = attributes
      .filter((attr) => ratings[attr.id])
      .map((attr) => ({
        name: attr.attributeDefinition.name,
        value: ratings[attr.id],
      }))
    setSubmittedOverall(overall)
    setSubmittedAttributes(breakdown)
    setStreak(nextStreak ?? null)
    setCollection(nextCollection ?? null)
    setTopOtherRatings(nextTopOther ?? [])
    setShowSuccessModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate all attributes are rated
    const missingRatings = attributes.filter((attr) => !ratings[attr.id])
    if (missingRatings.length > 0) {
      setError('Please rate all attributes')
      return
    }

    if (!skipRecaptcha && !siteKey) {
      setError('reCAPTCHA is not configured. Please try again later.')
      return
    }

    if (isGuest && guestAlreadyRated) {
      setShowAuthModal(true)
      return
    }

    if (!skipRecaptcha && !captchaToken) {
      setError('Please complete the reCAPTCHA challenge.')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/ratings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityId,
          attributeRatings: Object.entries(ratings).map(([entityAttributeId, value]) => ({
            entityAttributeId,
            value,
          })),
          comment: comment.trim() || undefined,
          method: 'SEARCH_AND_RATE',
          recaptchaToken: captchaToken,
        }),
      })

      const data = await response.json().catch(() => ({}))
      if (data.guest && response.ok) {
        track(EVT.RATE_SUBMIT, {
          entity_id: entityId,
          entity_type: entityType,
          entity_name: entityName,
          attribute_count: Object.keys(ratings).length,
          average_value: Number(calculateOverallRating()),
          guest: true,
        })
        openSuccessDialog()
        setRatings({})
        setComment('')
        recaptchaRef.current?.reset()
        setCaptchaToken(null)
        router.refresh()
        return
      }

      if (data.requiresAuth) {
        setShowAuthModal(true)
        recaptchaRef.current?.reset()
        setCaptchaToken(null)
        return
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit rating')
      }

      track(EVT.RATE_SUBMIT, {
        entity_id: entityId,
        entity_type: entityType,
        entity_name: entityName,
        attribute_count: Object.keys(ratings).length,
        average_value: Number(calculateOverallRating()),
      })

      openSuccessDialog(data.streak ?? null, data.collection ?? null, data.topOtherRatings ?? [])

      // Reset form
      setRatings({})
      setComment('')
      recaptchaRef.current?.reset()
      setCaptchaToken(null)

      if (onSuccess) {
        onSuccess()
      } else {
        router.refresh()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit rating')
      recaptchaRef.current?.reset()
      setCaptchaToken(null)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="pb-4 border-b border-[#1E4289]/15">
        <p className="text-md font-semibold uppercase tracking-wider text-[#E79D20] mb-1">
          Rate this
        </p>
        <h3 className="text-2xl sm:text-3xl font-bold text-[#1E4289] mb-1">
          How was {entityName}?
        </h3>
        <p className="text-sm text-slate-600">
          Score each attribute, then hit submit
        </p>
      </div>

      {/* Attribute Ratings */}
      <div className="space-y-4">
        {attributes.map((attr, attrIndex) => {
          const currentRating = ratings[attr.id] || 0
          const hoveredRating = hoveredStars[attr.id] || 0
          const isFirstRow = attrIndex === 0
          const demoRating =
            isFirstRow && demoActive && !currentRating && !hoveredRating ? demoCount : 0

          return (
            <div
              key={attr.id}
              className="space-y-2 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <label className="font-semibold text-sm text-slate-800 block">
                    {attr.attributeDefinition.name}
                  </label>
                  {attr.attributeDefinition.description && (
                    <span className="text-xs text-slate-500 block mt-1">
                      {attr.attributeDefinition.description}
                    </span>
                  )}
                </div>
                {currentRating > 0 && (
                  <span className="text-sm font-bold text-[#1E4289] ml-2">
                    {currentRating} / 5
                  </span>
                )}
              </div>

              {/* Star Rating - Blue squares with white stars, yellow glow on selection */}
              <div
                className="flex gap-1.5"
                onMouseLeave={() => handleStarLeave(attr.id)}
              >
                {[1, 2, 3, 4, 5].map((star) => {
                  const isSelected = star <= currentRating
                  const isHovered = hoveredRating > 0 && star <= hoveredRating
                  const isDemo = demoRating > 0 && star <= demoRating
                  const showGlow = isSelected || isHovered || isDemo
                  const isDemoLeading = isDemo && star === demoRating
                  // Calculate yellow intensity based on star position (1st star = lightest, 5th = brightest)
                  const yellowIntensity = showGlow ? Math.min(100, 40 + (star * 12)) : 0

                  return (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRatingChange(attr.id, star)}
                      onMouseEnter={() => handleStarHover(attr.id, star)}
                      className="focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FEDB1A] rounded"
                      aria-label={`Rate ${star} out of 5`}
                    >
                      <div
                        className={`w-11 h-11 flex items-center justify-center rounded transition-all duration-300 ${
                          showGlow ? 'bg-[#1E4289]' : 'bg-[#1E4289]/80'
                        } ${isDemoLeading ? 'scale-110' : ''}`}
                        style={showGlow ? {
                          boxShadow: `0 0 ${8 + star * 2}px ${star}px rgba(254, 219, 26, ${yellowIntensity / 100})`,
                        } : undefined}
                      >
                        <svg
                          className={`w-6 h-6 transition-all duration-300 ${
                            showGlow
                              ? 'fill-[#FEDB1A] text-[#FEDB1A] drop-shadow-[0_0_4px_rgba(254,219,26,0.8)]'
                              : 'fill-white text-white'
                          }`}
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                          />
                        </svg>
                      </div>
                    </button>
                  )
                })}
              </div>

              {/* Average rating display */}
              {attr.totalRatings > 0 && (
                <p className="text-xs text-slate-500">
                  Community average: {attr.averageRating.toFixed(1)} <BsStar className="inline text-[#E79D20]" /> ({attr.totalRatings} ratings)
                </p>
              )}
            </div>
          )
        })}
      </div>

      {/* Overall Rating Preview */}
      {Object.keys(ratings).length > 0 && (
        <div className="p-4 rounded-lg border border-[#FEDB1A]/60 bg-[#FEDB1A]/15">
          <p className="text-sm font-medium text-slate-700">
            Overall Rating:{' '}
            <span className="text-2xl font-bold text-[#1E4289]">{calculateOverallRating()}</span>
            <span className="text-slate-500"> / 5.0</span>
          </p>
        </div>
      )}

      {/* Comment */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Comment (optional)
        </label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 bg-white text-slate-800 placeholder:text-slate-400 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#1E4289] focus:border-transparent"
          placeholder="Share your thoughts..."
        />
      </div>

      {/* reCAPTCHA */}
      {!skipRecaptcha && (
        <div>
          {siteKey ? (
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={siteKey}
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
              onErrored={() => setCaptchaToken(null)}
            />
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              reCAPTCHA is not configured (missing NEXT_PUBLIC_RECAPTCHA_SITE_KEY).
            </p>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting || !captchaReady || Object.keys(ratings).length !== attributes.length}
        className="w-full px-6 py-4 bg-[#FEDB1A] text-[#1E4289] rounded-lg hover:bg-[#1E4289] hover:text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed font-bold text-lg shadow-lg transition-colors"
      >
        {isSubmitting ? 'Submitting...' : 'Submit Rating'}
      </button>
      <AuthPromptModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        entityName={entityName}
      />
      <RatingSuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        entityId={entityId}
        entitySlug={entitySlug}
        entityName={entityName}
        overallRating={submittedOverall}
        attributeRatings={submittedAttributes}
        suggestions={suggestions}
        isGuest={isGuest}
        streak={streak}
        collection={collection}
        topOtherRatings={topOtherRatings}
      />
    </form>
  )
}
